import type { CSSProperties } from 'react';
import { Outlet, useMatch } from 'react-router';
import { ResizeHandle } from '../../components';
import { cx } from '../../lib/cx';
import { useStoredNumber } from '../../lib/useStoredNumber';
import { ThreadSidebar } from './ThreadSidebar';

const SIDEBAR_DEFAULT = 336;
const SIDEBAR_MIN = 256;
const SIDEBAR_MAX = 560;
const clampSidebar = (value: number) => Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, value));

/**
 * Home: the thread list on the left, the open thread (or a new one) on the
 * right — the shape of every chat app, because that is what Home now is.
 *
 * The divider between them drags (and arrow-keys, and double-clicks back),
 * and the width is remembered in this browser: long titles want a wide list,
 * a long conversation wants a narrow one, and only the reader knows which
 * they are doing.
 *
 * Below md there is room for one of the two, so the route decides which: the
 * list at `/`, the thread alone at `/t/:id` (with a way back), and the
 * composer alone at `/new`. The stored width only applies from md up.
 */
export function ThreadsLayout() {
  const onThread = useMatch('/t/:threadId') !== null;
  const onNew = useMatch('/new') !== null;
  const paneOnly = onThread || onNew;
  const [width, setWidth, commitWidth] = useStoredNumber(
    'rundown.threads.sidebarWidth',
    SIDEBAR_DEFAULT,
    clampSidebar,
  );

  return (
    <div className="flex h-full min-h-0" style={{ '--sidebar-w': `${width}px` } as CSSProperties}>
      <div
        className={cx(
          'min-h-0 w-full flex-none md:flex md:w-[var(--sidebar-w)]',
          paneOnly ? 'hidden' : 'flex',
        )}
      >
        <ThreadSidebar />
      </div>
      <ResizeHandle
        label="Resize thread list"
        value={width}
        min={SIDEBAR_MIN}
        max={SIDEBAR_MAX}
        step={16}
        onChange={setWidth}
        onCommit={commitWidth}
        onReset={() => commitWidth(SIDEBAR_DEFAULT)}
        className="z-10 -mx-1 hidden w-2 md:block"
      />
      <div className={cx('min-h-0 min-w-0 flex-1 flex-col', paneOnly ? 'flex' : 'hidden md:flex')}>
        <Outlet />
      </div>
    </div>
  );
}
